/**
 * Local fetch-based service for the XEPPT merchant-scoped "Transactions" list.
 * Does NOT use the xeppt-sdk — POST /transactions/business is a brand-new
 * endpoint (2026-07-13 merchant-tabs redesign), not yet published in
 * @xeppt/xeppt-sdk's TransactionService. Same situation as refund_service.ts
 * and order_block_service.ts.
 *
 * AUTH: Bearer access token from localStorage 'client-metadata', same
 * pattern as refund_service.ts and order_block_service.ts.
 *
 * API CONTRACT (backend live, see main-api-webhooks
 * internal/http/modules/transactions/transactions.go):
 *   POST /transactions/business
 *   Body: { search?, status?, type?, method?, action?, date_from?, date_to?,
 *           page?, page_size?, cursor? } — businessUserID is bound
 *   server-side from the JWT, never sent in the body.
 *   → { data: TTransactionWithReferenceCode[], page, page_size, pages_total,
 *       total, cursor? }
 *
 * Returns merchant-scoped transactions that are NEITHER order-associated
 * (the Sales tab) NOR external-request-derived (the External requests tab) —
 * see transactions_business_list.go on the backend for the full partition
 * rationale. consumer_id/consumer_name/consumer_email/consumer_phone are
 * always empty strings on every row (merchant's own wallet leg only).
 *
 * PAGINATION (2026-07-13, cursor support added, backend commit e03565c2):
 * two mutually-exclusive modes selected by whether `cursor` is present in
 * the REQUEST body — there is no separate "mode" flag.
 *   - `cursor` omitted -> offset mode (page/page_size), the dashboard
 *     mini-tab's exact existing call (src/UI/sections/dashboard/transactions)
 *     — response `cursor` is always absent (omitted) in this mode.
 *   - `cursor` supplied -> keyset mode (only rows with created_at strictly
 *     before `cursor` are returned) — for the "View all" infinite-scroll
 *     page. Response `cursor` is then always present: the last returned
 *     row's created_at, or an echo of the request cursor once the scroll is
 *     exhausted (empty page).
 * IMPORTANT: omitting `cursor` on the FIRST infinite-scroll request does NOT
 * start a cursor-mode scroll — it engages offset mode instead, and the
 * response will never carry a `cursor` to continue from (confirmed against
 * actions_api.ListTransactionsBusiness: `nextCursor` stays nil whenever
 * `input.Cursor == nil`, unconditionally). A cursor-based scroll must seed a
 * real value on its first request — the same "now" sentinel convention
 * apiTransactionsService.findTransactions already uses — never omit it once
 * you intend to page by cursor.
 */
import type {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType
} from '@xeppt/xeppt-sdk/types';
import type { TTransactionWithReferenceCode } from 'src/types/reference_code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Error class ─────────────────────────────────────────────────────────────
// Named `status`/`code` (not `errorCode`/`statusCode`) to match the other
// local (non-SDK) service error classes in this directory.

export class TransactionsBusinessApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'TransactionsBusinessApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new TransactionsBusinessApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        code
    );
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ListTransactionsBusinessParams {
    search?: string;
    status?: ETransactionStatus[];
    type?: ETransactionType[];
    method?: ETransactionMethod[];
    action?: ETransactionAction[];
    date_from?: Date | string;
    date_to?: Date | string;
    page?: number;
    page_size?: number;
    // Optional keyset-pagination cursor for the "View all" infinite-scroll
    // page. Omit entirely for offset (page/page_size) mode — see the
    // PAGINATION note above. Accepts a Date (JSON.stringify serializes it to
    // an ISO string via Date.prototype.toJSON) or a raw string, e.g. echoing
    // a prior response's `cursor` back in unmodified.
    cursor?: Date | string;
}

export interface ListTransactionsBusinessResponse {
    data: TTransactionWithReferenceCode[];
    page: number;
    page_size: number;
    pages_total: number;
    total?: number;
    // Present ONLY when the request supplied a cursor (keyset mode);
    // omitted in offset mode. Arrives as an RFC3339 STRING over the wire
    // (Go marshals time.Time to a JSON string) — NEVER a Date instance.
    // Treat it as an opaque string: pass it back verbatim as the next
    // request's `cursor` and never call Date methods on it directly.
    cursor?: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const apiTransactionsBusinessService = {
    /**
     * POST /transactions/business — paginated merchant-scoped transactions,
     * excluding order-associated (Sales) and external-request rows. No user
     * id is sent: the backend binds businessUserID from the JWT.
     */
    async listTransactions(
        params: ListTransactionsBusinessParams = {}
    ): Promise<ListTransactionsBusinessResponse> {
        const response = await fetch(`${BACKEND_URL}/transactions/business`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(params)
        });
        if (!response.ok) {
            await throwOnError(response);
        }
        return (await response.json()) as ListTransactionsBusinessResponse;
    }
};
